import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getReadableTextColor } from 'js/utils/color';
import CapabilityChildNodes from './CapabilityChildNodes';
import {
  CAPABILITY_RADIUS,
  HEADER_HEIGHT_LEAF,
  HEADER_HEIGHT_WITH_CHILDREN,
  LABEL_INSET,
  TEXT_DARK_HEX,
  TEXT_FOREGROUND_ALPHA,
  TILE_INSET,
  UNSCORED_FILL,
  UNSCORED_STROKE,
  UNSCORED_STROKE_WIDTH,
  formatScore,
  scoreFill,
  scoreIcon,
} from './capabilityTreemapUtils';
import styles from './CapabilityTreemap.module.scss';

export default function CapabilityNode({
  node,
  minLabelWidth,
  minLabelHeight,
  onCapabilityHover,
  onCapabilityLeave,
}) {
  const nodeWidth = Math.max(0, node.x1 - node.x0);
  const nodeHeight = Math.max(0, node.y1 - node.y0);
  const score = node.data.score;
  const fillColor = scoreFill(score);
  const textColor = getReadableTextColor(fillColor, {
    dark: TEXT_DARK_HEX,
    foregroundAlpha: TEXT_FOREGROUND_ALPHA,
  });
  const name = node.data.name || 'Capability';
  const children = node.data.childCapabilities || [];
  const hasChildren = children.length > 0;
  const labelMinHeight = hasChildren
    ? HEADER_HEIGHT_WITH_CHILDREN
    : minLabelHeight;
  const canShowLabel =
    nodeWidth >= minLabelWidth && nodeHeight >= labelMinHeight;
  const showMetrics = canShowLabel && !hasChildren;
  const labelHeight = canShowLabel
    ? hasChildren
      ? HEADER_HEIGHT_WITH_CHILDREN
      : HEADER_HEIGHT_LEAF
    : 0;
  const childAreaX = node.x0 + TILE_INSET;
  const childAreaY = node.y0 + TILE_INSET + labelHeight;
  const childAreaWidth = Math.max(0, nodeWidth - TILE_INSET * 2);
  const childAreaHeight = Math.max(
    0,
    nodeHeight - TILE_INSET * 2 - labelHeight
  );
  const needsBorder = fillColor === UNSCORED_FILL;
  const hoverHandlers = hasChildren
    ? {}
    : {
        onMouseMove: (event) =>
          onCapabilityHover(event, {
            label: name,
            score,
            groupCount: node.data.groupCount,
          }),
        onMouseLeave: onCapabilityLeave,
      };

  return (
    <g>
      {needsBorder ? (
        <>
          <rect
            x={node.x0}
            y={node.y0}
            width={nodeWidth}
            height={nodeHeight}
            fill={UNSCORED_STROKE}
            stroke="none"
            rx={CAPABILITY_RADIUS}
            {...hoverHandlers}
          />
          <rect
            x={node.x0 + UNSCORED_STROKE_WIDTH}
            y={node.y0 + UNSCORED_STROKE_WIDTH}
            width={Math.max(0, nodeWidth - UNSCORED_STROKE_WIDTH * 2)}
            height={Math.max(0, nodeHeight - UNSCORED_STROKE_WIDTH * 2)}
            fill={fillColor}
            stroke="none"
            rx={Math.max(0, CAPABILITY_RADIUS - UNSCORED_STROKE_WIDTH)}
            pointerEvents="none"
          />
        </>
      ) : (
        <rect
          x={node.x0}
          y={node.y0}
          width={nodeWidth}
          height={nodeHeight}
          fill={fillColor}
          stroke="none"
          rx={CAPABILITY_RADIUS}
          {...hoverHandlers}
        />
      )}
      <CapabilityChildNodes
        childAreaX={childAreaX}
        childAreaY={childAreaY}
        childAreaWidth={childAreaWidth}
        childAreaHeight={childAreaHeight}
        childCapabilities={children}
        minLabelWidth={minLabelWidth}
        minLabelHeight={minLabelHeight}
        onCapabilityHover={onCapabilityHover}
        onCapabilityLeave={onCapabilityLeave}
      />
      {labelHeight > 0 && (
        <rect
          x={node.x0}
          y={node.y0}
          width={nodeWidth}
          height={labelHeight}
          fill="transparent"
          onMouseMove={(event) =>
            onCapabilityHover(event, {
              label: name,
              score,
              groupCount: node.data.groupCount,
            })
          }
          onMouseLeave={onCapabilityLeave}
        />
      )}
      {canShowLabel && (
        <foreignObject
          x={node.x0 + LABEL_INSET}
          y={node.y0 + LABEL_INSET}
          width={Math.max(0, nodeWidth - LABEL_INSET * 2)}
          height={Math.max(0, labelHeight - LABEL_INSET + TILE_INSET)}
          pointerEvents="none"
        >
          <div
            xmlns="http://www.w3.org/1999/xhtml"
            className={styles['tile-label']}
            style={{ color: textColor }}
          >
            <div className={styles['tile-title']} title={name}>
              {name}
            </div>
            {showMetrics && (
              <div className={styles['metric-row']}>
                <FontAwesomeIcon
                  icon={scoreIcon(score)}
                  className={styles['metric-icon']}
                  fixedWidth
                />
                <span className={styles['metric-value']}>
                  {formatScore(score)}
                </span>
              </div>
            )}
          </div>
        </foreignObject>
      )}
    </g>
  );
}
